import React, { useEffect, useState } from 'react';
import { FunctionsHttpError } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Mail, Reply, Send, X } from 'lucide-react';

interface Enquiry {
  id: string; name: string; email: string; subject: string; message: string; created_at: string;
}

async function call(body: Record<string, unknown>) {
  const { data, error } = await supabase.functions.invoke('reply-enquiry', { body });
  if (error) {
    const details = error instanceof FunctionsHttpError ? await error.context.text() : error.message;
    throw new Error(details);
  }
  return data;
}

const EnquiriesAdmin: React.FC = () => {
  const [items, setItems] = useState<Enquiry[]>([]);
  const [from, setFrom] = useState('');
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState<string | null>(null);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    call({ action: 'list' })
      .then((d) => { setItems(d.items ?? []); setFrom(d.from ?? ''); })
      .catch((e) => toast({ title: 'Could not load enquiries', description: String(e.message), variant: 'destructive' }))
      .finally(() => setLoading(false));
  }, []);

  const startReply = (e: Enquiry) => {
    setOpen(e.id);
    setSubject(e.subject?.toLowerCase().startsWith('re:') ? e.subject : `Re: ${e.subject || 'Your enquiry'}`);
    setMessage(`Dear ${e.name},\n\n`);
  };

  const send = async (id: string) => {
    setSending(true);
    try {
      await call({ action: 'reply', id, subject, message });
      toast({ title: 'Reply sent', description: `Sent from ${from}` });
      setOpen(null);
    } catch (e: any) {
      toast({ title: 'Reply failed', description: String(e.message), variant: 'destructive' });
    } finally {
      setSending(false);
    }
  };

  if (loading) return <p className="text-white py-8 text-center">Loading enquiries...</p>;

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-white">Enquiries</h2>
        {from && <p className="text-sm text-gray-400">Replies are sent from {from}; answers come back to info@aiconference.london.</p>}
      </div>
      {items.length === 0 ? (
        <div className="text-center py-12 bg-white/5 rounded-lg border border-purple-500/20">
          <Mail className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <p className="text-gray-400">No enquiries yet.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {items.map((e) => (
            <Card key={e.id} className="bg-white/5 border-purple-500/20">
              <CardContent className="p-5 space-y-3">
                <div className="flex flex-wrap justify-between gap-2">
                  <div>
                    <p className="font-semibold text-white">{e.subject}</p>
                    <p className="text-sm text-gray-400">{e.name} · {e.email} · {new Date(e.created_at).toLocaleString()}</p>
                  </div>
                  {open !== e.id && (
                    <Button size="sm" onClick={() => startReply(e)} className="bg-purple-600 hover:bg-purple-700">
                      <Reply className="mr-2 h-4 w-4" /> Reply
                    </Button>
                  )}
                </div>
                <p className="text-gray-300 whitespace-pre-wrap">{e.message}</p>
                {open === e.id && (
                  <div className="space-y-3 pt-3 border-t border-white/10">
                    <Input value={subject} onChange={(ev) => setSubject(ev.target.value)} placeholder="Subject" />
                    <Textarea value={message} onChange={(ev) => setMessage(ev.target.value)} rows={8} />
                    <div className="flex gap-2 justify-end">
                      <Button variant="outline" onClick={() => setOpen(null)}><X className="mr-2 h-4 w-4" /> Cancel</Button>
                      <Button disabled={sending || !message.trim()} onClick={() => send(e.id)} className="bg-purple-600 hover:bg-purple-700">
                        <Send className="mr-2 h-4 w-4" /> {sending ? 'Sending...' : 'Send reply'}
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default EnquiriesAdmin;
